import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Card, Button, Table, Tag, Badge, Space, Modal, Tour, Select, Input, message } from "antd";
import { ArrowLeftOutlined, SyncOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import { useCreate } from "../../../hooks";
import { mawbStockService, mawbUsageService } from "../../../api/enhancedApi";
import { useInfiniteList } from "../hooks/useInfiniteList";
import "./MawbStockDetailPage.css";
import { formatDateTime } from "../../../utils/dateUtils";
import PermissionGuard from "../../../components/auth/PermissionGuard";
import FilteredExportButton from "../../../components/export/FilteredExportButton";
import MawbAllocationModal from "../MawbStockList/components/MawbAllocationModal";

// 详情页单号「状态」下拉 → 后端筛选参数映射（与表格 renderUsageState 的展示口径一致）
const USAGE_STATE_OPTIONS = [
    { value: "unallocated", label: "未分配", params: { status: "AVAILABLE", allocation_state: "unallocated" } },
    { value: "allocated", label: "已分配", params: { status: "AVAILABLE", allocation_state: "allocated" } },
    { value: "used", label: "已使用", params: { status: "USED" } },
    { value: "void", label: "已作废", params: { status: "VOID" } },
];

// 「分配日期」下拉里“全部”的哨兵值：查询上等同不限日期，但对导出而言是一次明确的选择
// （导出前必须选定具体日期或“全部”，不允许什么都没选就导出）。
const ALL_ALLOCATION_DATES = "__ALL__";
const USAGE_PAGE_SIZE = 100;

const stockStatusMap = {
    AVAILABLE: { text: "可用", status: "success" },
    PARTIALLY_ALLOCATED: { text: "部分分配", status: "warning" },
    FULLY_ALLOCATED: { text: "全部分配", status: "default" },
};

const MawbStockDetailPage = () => {
    const { id } = useParams();
    const stockId = useMemo(() => {
        const parsed = parseInt(id, 10);
        return Number.isNaN(parsed) ? null : parsed;
    }, [id]);
    const navigate = useNavigate();

    const [stock, setStock] = useState(null);
    const [stockLoading, setStockLoading] = useState(false);
    const [summary, setSummary] = useState([]);
    const usageTableRef = useRef(null);
    const [usageTableScrollY, setUsageTableScrollY] = useState(420);
    const [loadingAllUsages, setLoadingAllUsages] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [allocModalOpen, setAllocModalOpen] = useState(false);
    const [allocUsageIds, setAllocUsageIds] = useState(null);
    const [allocAutoGuide, setAllocAutoGuide] = useState(false);
    const [tourOpen, setTourOpen] = useState(false);

    // 筛选条件（联动表格查询与导出）
    const [filterName, setFilterName] = useState();
    const [filterState, setFilterState] = useState();
    const [filterMawb, setFilterMawb] = useState(""); // 已应用的单号搜索词
    const [mawbInput, setMawbInput] = useState(""); // 输入框内当前文本（回车/点击搜索后才应用）
    // 分配日期筛选：undefined=未选择（表格不限日期，但导出会被拦截要求先选）；
    // ALL_ALLOCATION_DATES=明确选了“全部”；其余为具体日期字符串（YYYY-MM-DD）
    const [filterDate, setFilterDate] = useState();
    const [allocationDates, setAllocationDates] = useState([]); // 号段内真实出现过的分配日期（含数量）

    // 页面内引导锚点
    const infoCardRef = useRef(null);
    const detailCardRef = useRef(null);
    const filterBarRef = useRef(null);
    const toolbarRef = useRef(null);
    const allocBtnRef = useRef(null);

    const loadStock = useCallback(async () => {
        if (stockId === null) return;
        setStockLoading(true);
        try {
            const res = await mawbStockService.getById(stockId);
            setStock(res.data);
        } catch {
            message.error("加载库存详情失败");
        } finally {
            setStockLoading(false);
        }
    }, [stockId]);

    const loadSummary = useCallback(async () => {
        if (stockId === null) return;
        try {
            const res = await mawbStockService.getAllocationSummary(stockId);
            setSummary(res.data?.items || []);
        } catch {
            setSummary([]);
        }
    }, [stockId]);

    // 分配日期下拉的候选项只来自后端统计的真实日期，不开放自由选日
    const loadAllocationDates = useCallback(async () => {
        if (stockId === null) return;
        try {
            const res = await mawbStockService.getAllocationDates(stockId);
            setAllocationDates(res.data?.items || []);
        } catch {
            setAllocationDates([]);
        }
    }, [stockId]);

    useEffect(() => {
        loadStock();
        loadSummary();
        loadAllocationDates();
    }, [loadStock, loadSummary, loadAllocationDates]);

    // 由筛选 UI 状态推导后端筛选参数（单号明细表格与导出共用同一份口径）
    const backendFilters = useMemo(() => {
        const f = {};
        if (filterName) f.allocated_to_name = filterName;
        if (filterMawb) f.full_mawb = filterMawb;
        const stateCfg = USAGE_STATE_OPTIONS.find((o) => o.value === filterState);
        if (stateCfg) Object.assign(f, stateCfg.params);
        // “全部”只是导出前的明确表态，查询上等同不限日期，不下发参数
        if (filterDate && filterDate !== ALL_ALLOCATION_DATES) f.allocation_date = filterDate;
        return f;
    }, [filterName, filterMawb, filterState, filterDate]);

    const hasActiveFilter = filterName || filterState || filterMawb || filterDate;

    const usageQuery = useMemo(
        () => ({
            stock_id: stockId,
            ...backendFilters,
        }),
        [stockId, backendFilters],
    );

    // 导出用筛选参数（不含分页，导出范围即当前筛选结果）
    const exportFilterParams = useMemo(
        () => ({ stock_id: stockId, ...backendFilters }),
        [stockId, backendFilters],
    );

    // 导出前检查：导出文件通常直接发给分配对象（外部公司），按与导出一致的筛选口径
    // 统计分配对象分布，弹确认拦两种情况：
    //  1) ≥2 个分配对象：发给其中一家会让对方看到别家公司的单号 → 泄密；
    //  2) 仅 1 个对象但夹带未分配：未泄露别家信息，但文件混进了不该发出去的未分配单号。
    const handleBeforeExport = useCallback(async () => {
        // 导出条件不允许为空：必须先选定分配日期，导出全部要明确选“全部”
        if (!filterDate) {
            message.warning("请先选择「分配日期」再导出；如需导出全部日期，请选择「全部」");
            return false;
        }
        try {
            const res = await mawbUsageService.getAllocationDistinct(exportFilterParams);
            const objects = res.data?.objects || [];
            const unallocatedCount = res.data?.unallocated_count || 0;

            const multiObject = objects.length >= 2; // 情况一：多个分配对象 → 泄密
            const singleWithUnalloc = objects.length === 1 && unallocatedCount > 0; // 情况二：夹带未分配
            if (!multiObject && !singleWithUnalloc) return true;

            const objectList = (
                <ul style={{ margin: "8px 0 0", paddingLeft: 18, maxHeight: 180, overflowY: "auto" }}>
                    {objects.map((o) => (
                        <li key={`${o.allocated_to_type}-${o.allocated_to_name}`}>
                            {o.allocated_to_type === "internal" ? "内部" : "合作伙伴"}：
                            {o.allocated_to_name}（{o.count} 个）
                        </li>
                    ))}
                    {unallocatedCount > 0 ? <li>未分配（{unallocatedCount} 个）</li> : null}
                </ul>
            );

            const title = multiObject ? "可能泄露其他公司的单号" : "夹带了未分配单号";
            const body = multiObject ? (
                <p style={{ margin: 0 }}>
                    导出文件通常会直接发给分配对象（外部公司）。当前导出内容包含下面 {objects.length} 个分配对象的单号，
                    一旦整份发给其中一家，对方就能看到其他公司分到了哪些主单号，造成商业泄密。
                    请先在上方「分配对象」选定一家、确认只剩这一家后再导出。
                </p>
            ) : (
                <p style={{ margin: 0 }}>
                    导出文件通常会直接发给分配对象（外部公司）。当前内容除了「{objects[0]?.allocated_to_name}」的单号，
                    还夹带了 {unallocatedCount} 个未分配单号，这些不应出现在发给对方的文件里。
                    可在上方「状态」选「已分配」后再导出，只发对方自己的单号。
                </p>
            );

            return await new Promise((resolve) => {
                Modal.confirm({
                    title,
                    width: 480,
                    okText: "仍要导出",
                    cancelText: "取消",
                    okButtonProps: { danger: true },
                    content: (
                        <div>
                            {body}
                            {objectList}
                        </div>
                    ),
                    onOk: () => resolve(true),
                    onCancel: () => resolve(false),
                });
            });
        } catch (e) {
            // 预检失败不阻断主流程：放行导出，避免附加校验失败导致无法导出
            console.warn("导出前分配对象预检失败，跳过提示:", e);
            return true;
        }
    }, [exportFilterParams, filterDate]);

    // 分配对象下拉项（取自分配统计，便于按合作伙伴快速筛选）
    const partnerOptions = useMemo(
        () =>
            summary.map((s) => ({
                value: s.allocated_to_name,
                label: `${s.allocated_to_type === "internal" ? "内部" : "合作伙伴"}：${s.allocated_to_name}`,
            })),
        [summary],
    );

    // 分配日期下拉项：“全部” + 号段内真实出现过的日期（最新在前，附数量）
    const dateOptions = useMemo(
        () => [
            { value: ALL_ALLOCATION_DATES, label: "全部" },
            ...allocationDates.map((d) => ({
                value: d.allocation_date,
                label: `${d.allocation_date}（${d.count} 个）`,
            })),
        ],
        [allocationDates],
    );

    const {
        items: usageItems,
        total: usageTotal,
        loading: usageLoading,
        loadingMore: usageLoadingMore,
        stale: usageStale,
        hasMore: usageHasMore,
        reload: refetchUsages,
        loadMore: loadMoreUsages,
        loadAll: loadAllUsages,
    } = useInfiniteList({
        fetcher: mawbUsageService.getList,
        query: usageQuery,
        pageSize: USAGE_PAGE_SIZE,
        enabled: stockId !== null,
        scrollBodyRef: usageTableRef,
    });

    const clearSelection = useCallback(() => {
        setSelectedRowKeys([]);
        setSelectedRows([]);
    }, []);

    useEffect(() => {
        clearSelection();
    }, [stockId, clearSelection]);

    const handleRefreshAll = useCallback(() => {
        loadStock();
        loadSummary();
        loadAllocationDates();
        refetchUsages();
        clearSelection();
    }, [loadStock, loadSummary, loadAllocationDates, refetchUsages, clearSelection]);

    const deallocateMutation = useCreate("收回主单号", mawbUsageService.deallocate, {
        onSuccess: () => handleRefreshAll(),
    });

    // 筛选变化会让明细从头加载，勾选也要清掉，避免选中已经不在当前结果里的行
    const resetToFirstPage = useCallback(() => {
        clearSelection();
    }, [clearSelection]);

    const handleResetFilters = useCallback(() => {
        setFilterName(undefined);
        setFilterState(undefined);
        setFilterMawb("");
        setMawbInput("");
        setFilterDate(undefined);
        resetToFirstPage();
    }, [resetToFirstPage]);

    const applyMawbSearch = useCallback(
        (value) => {
            setFilterMawb((value || "").trim());
            resetToFirstPage();
        },
        [resetToFirstPage],
    );

    // ── 分配入口 ──
    const openAllocateByQuantity = () => {
        setAllocUsageIds(null);
        setAllocModalOpen(true);
    };
    const openAllocateRow = (record) => {
        setAllocUsageIds([record.id]);
        setAllocModalOpen(true);
    };
    const selectionCoversLoadedRows = () => {
        const selectableCount = usageItems.filter((row) => row.status === "AVAILABLE").length;
        return usageHasMore && selectableCount > 0 && selectedRowKeys.length >= selectableCount;
    };

    const warnIfListStale = () => {
        if (!usageStale) return false;
        message.warning("列表没有刷新成功，请先点刷新，再分配或收回");
        return true;
    };

    const handleLoadAllUsages = async () => {
        if (loadingAllUsages || usageLoading) return;
        if (usageStale) {
            message.warning("列表没有刷新成功，请先点刷新");
            return;
        }
        setLoadingAllUsages(true);
        try {
            const done = await loadAllUsages();
            if (!done) message.warning("还有单号没加载完，可以再点一次");
        } finally {
            setLoadingAllUsages(false);
        }
    };

    const openBatchAllocate = () => {
        if (warnIfListStale()) return;
        const ids = selectedRows
            .filter((r) => !r.allocated_to_name && r.status === "AVAILABLE")
            .map((r) => r.id);
        if (!ids.length) {
            message.warning("选中项中没有可分配的主单号");
            return;
        }
        const open = () => {
            setAllocUsageIds(ids);
            setAllocModalOpen(true);
        };
        if (!selectionCoversLoadedRows()) {
            open();
            return;
        }
        Modal.confirm({
            title: "还有没加载的单号",
            content: `筛选结果共 ${usageTotal} 条，表格里目前是 ${usageItems.length} 条。这次只会分配已勾选的 ${ids.length} 个。要带上后面的单号，请先加载全部，再重新全选。`,
            okText: "只分配已勾选",
            cancelText: "取消",
            onOk: open,
        });
    };

    // ── 收回 ──
    const doDeallocate = (ids) => {
        if (!ids.length) {
            message.warning("没有可收回的主单号");
            return;
        }
        deallocateMutation.mutate({ usage_ids: ids });
    };
    const handleDeallocateRow = (record) => {
        Modal.confirm({
            title: `确认收回主单号 ${record.full_mawb}？`,
            content: "收回后该主单号将变回未分配状态",
            okText: "收回",
            cancelText: "取消",
            onOk: () => doDeallocate([record.id]),
        });
    };
    const handleBatchDeallocate = () => {
        if (warnIfListStale()) return;
        const ids = selectedRows
            .filter((r) => r.allocated_to_name && r.status === "AVAILABLE")
            .map((r) => r.id);
        if (!ids.length) {
            message.warning("选中项中没有可收回的主单号");
            return;
        }
        const coversLoaded = selectionCoversLoadedRows();
        Modal.confirm({
            title: coversLoaded ? "还有没加载的单号" : `确认收回选中的 ${ids.length} 个主单号？`,
            content: coversLoaded
                ? `筛选结果共 ${usageTotal} 条，表格里目前是 ${usageItems.length} 条。这次只会收回已勾选的 ${ids.length} 个。要带上后面的单号，请先加载全部，再重新全选。`
                : "收回后将变回未分配状态",
            okText: coversLoaded ? "只收回已勾选" : "收回",
            cancelText: "取消",
            onOk: () => doDeallocate(ids),
        });
    };

    const statusCfg = stockStatusMap[stock?.status] || { text: stock?.status, status: "default" };
    const allocatedCount = stock ? stock.quantity - stock.available_quantity : 0;

    useEffect(() => {
        const updateTableHeight = () => {
            const node = usageTableRef.current;
            if (!node) return;
            const top = node.getBoundingClientRect().top;
            setUsageTableScrollY(Math.max(240, Math.floor(window.innerHeight - top - 72)));
        };
        updateTableHeight();
        window.addEventListener("resize", updateTableHeight);
        const observed = infoCardRef.current;
        const observer = observed && typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateTableHeight) : null;
        if (observer && observed) observer.observe(observed);
        return () => {
            window.removeEventListener("resize", updateTableHeight);
            if (observer) observer.disconnect();
        };
    }, [stockLoading, summary.length, hasActiveFilter]);

    const renderUsageState = (record) => {
        if (record.status === "VOID") return <Tag color="red">已作废</Tag>;
        if (record.status === "USED") return <Tag color="purple">已使用</Tag>;
        if (record.allocated_to_name) return <Tag color="blue">已分配</Tag>;
        return <Tag color="green">未分配</Tag>;
    };

    const columns = [
        { title: "完整单号", dataIndex: "full_mawb", key: "full_mawb", width: 150 },
        { title: "校验位", dataIndex: "check_digit", key: "check_digit", width: 70, align: "center" },
        { title: "状态", key: "state", width: 100, align: "center", render: (_, r) => renderUsageState(r) },
        {
            title: "分配对象", key: "allocated_to", width: 150,
            render: (_, r) =>
                r.allocated_to_name
                    ? `${r.allocated_to_type === "internal" ? "内部" : "合作伙伴"}：${r.allocated_to_name}`
                    : "-",
        },
        { title: "分配日期", dataIndex: "allocation_date", key: "allocation_date", width: 120, render: (v) => v || "-" },
        { title: "使用时间", dataIndex: "used_at", key: "used_at", width: 160, render: (v) => (v ? formatDateTime(v) : "-") },
        {
            title: "操作", key: "action", fixed: "right", width: 90, align: "center",
            render: (_, record) => {
                if (record.status !== "AVAILABLE") return "-";
                if (!record.allocated_to_name) {
                    return (
                        <PermissionGuard permission="mawb:allocate">
                            <Button type="link" size="small" onClick={() => openAllocateRow(record)}>
                                分配
                            </Button>
                        </PermissionGuard>
                    );
                }
                return (
                    <PermissionGuard permission="mawb:allocate">
                        <Button type="link" size="small" danger onClick={() => handleDeallocateRow(record)}>
                            收回
                        </Button>
                    </PermissionGuard>
                );
            },
        },
    ];

    const rowSelection = {
        selectedRowKeys,
        onChange: (keys, rows) => {
            setSelectedRowKeys(keys);
            setSelectedRows(rows);
        },
        getCheckboxProps: (record) => ({ disabled: record.status !== "AVAILABLE" }),
    };

    const tourSteps = [
        {
            title: "库存概览",
            description: "号段的前缀、范围和数量收在这一行。下面的名字可以点，只看这个对象分到的单号。",
            target: () => infoCardRef.current,
        },
        {
            title: "主单明细",
            description: "表格逐行列出每个主单号、校验位、当前状态（未分配 / 已分配 / 已使用 / 已作废）与分配对象。",
            target: () => detailCardRef.current,
        },
        {
            title: "筛选与导出",
            description: "按「分配对象 / 状态 / 分配日期 / 单号」筛选后，点「导出Excel」即可把当前筛选结果导出。分配日期只列出本号段真实出现过的日期；导出前必须选定日期（导出全部请明确选「全部」）。",
            target: () => filterBarRef.current,
        },
        {
            title: "勾选与批量操作",
            description: "勾选多行后，可「批量分配」给同一对象，或「批量收回」让它们变回未分配。",
            target: () => toolbarRef.current,
        },
        {
            title: "逐行分配 / 收回",
            description: "也可在每行操作列单独「分配」；已分配但尚未使用的主单号可随时「收回」。",
            target: () => detailCardRef.current,
        },
        {
            title: "按数量分配",
            description: "不想逐个勾选？点右上「分配号码」按数量分配，系统自动取前 N 个未分配主单号。点「打开并演示」逐字段带你填。",
            target: () => allocBtnRef.current,
            nextButtonProps: { children: "打开并演示" },
        },
    ];

    return (
        <div className="page-container mawb-detail">
            <div className="page-header-compact">
                <div className="page-title">
                    <Space>
                        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/mawb/stock")}>
                            返回列表
                        </Button>
                        <h2 style={{ margin: 0 }}>
                            {stock ? `${stock.airline_prefix}  ${stock.start_serial}–${stock.end_serial}` : "库存详情"}
                        </h2>
                        {stock ? <Badge status={statusCfg.status} text={statusCfg.text} /> : null}
                    </Space>
                </div>
                <div className="page-actions">
                    <Space>
                        <Button icon={<QuestionCircleOutlined />} onClick={() => setTourOpen(true)}>
                            使用教程
                        </Button>
                        <Button
                            icon={<SyncOutlined />}
                            onClick={handleRefreshAll}
                            loading={stockLoading || usageLoading}
                        >
                            刷新
                        </Button>
                        <PermissionGuard permission="mawb:allocate">
                            <Button
                                ref={allocBtnRef}
                                type="primary"
                                disabled={!stock || stock.status === "FULLY_ALLOCATED"}
                                onClick={openAllocateByQuantity}
                            >
                                分配号码
                            </Button>
                        </PermissionGuard>
                    </Space>
                </div>
            </div>

            <div ref={infoCardRef} className="mawb-detail__facts">
                {stockLoading && !stock ? <span className="mawb-detail__muted">正在加载号段…</span> : null}
                {stock ? (
                    <>
                        <div className="mawb-detail__counts">
                            <span>共 {stock.quantity}</span>
                            <span>未分配 {stock.available_quantity}</span>
                            <span>已分配 {allocatedCount}</span>
                            <span className="mawb-detail__muted">收到 {stock.received_date || "—"}</span>
                        </div>
                        <div className="mawb-detail__alloc">
                            {summary.length === 0 ? (
                                <span className="mawb-detail__muted">尚未分配给任何人</span>
                            ) : (
                                summary.map((s) => {
                                    const selected = filterName === s.allocated_to_name;
                                    return (
                                        <Tag
                                            key={`${s.allocated_to_type}-${s.allocated_to_name}`}
                                            color={selected ? "blue" : s.allocated_to_type === "internal" ? "gold" : "default"}
                                            className="mawb-detail__alloc-tag"
                                            onClick={() => {
                                                setFilterName(selected ? undefined : s.allocated_to_name);
                                                resetToFirstPage();
                                            }}
                                        >
                                            {s.allocated_to_type === "internal" ? "内部" : "伙伴"} {s.allocated_to_name} {s.count}
                                        </Tag>
                                    );
                                })
                            )}
                        </div>
                    </>
                ) : null}
            </div>

            <div ref={detailCardRef}>
            <Card className="data-card mawb-detail__work">
                <div ref={filterBarRef} className="mawb-detail__toolbar">
                    <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        placeholder="分配对象"
                        style={{ width: 200 }}
                        value={filterName}
                        options={partnerOptions}
                        onChange={(v) => {
                            setFilterName(v);
                            resetToFirstPage();
                        }}
                    />
                    <Select
                        allowClear
                        placeholder="状态"
                        style={{ width: 130 }}
                        value={filterState}
                        options={USAGE_STATE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                        onChange={(v) => {
                            setFilterState(v);
                            resetToFirstPage();
                        }}
                    />
                    <Select
                        allowClear
                        placeholder="分配日期（导出必选）"
                        style={{ width: 190 }}
                        value={filterDate}
                        options={dateOptions}
                        onChange={(v) => {
                            setFilterDate(v);
                            resetToFirstPage();
                        }}
                    />
                    <Input.Search
                        allowClear
                        placeholder="单号搜索"
                        style={{ width: 200 }}
                        value={mawbInput}
                        onChange={(e) => {
                            const v = e.target.value;
                            setMawbInput(v);
                            if (v === "" && filterMawb) applyMawbSearch("");
                        }}
                        onSearch={applyMawbSearch}
                    />
                    {hasActiveFilter ? (
                        <Button size="small" onClick={handleResetFilters}>
                            重置
                        </Button>
                    ) : null}
                    <span className="mawb-detail__muted">
                        {usageStale
                            ? "刷新没有成功，表格里还是上一次的结果"
                            : `已加载 ${usageItems.length} / 共 ${usageTotal} 条${hasActiveFilter ? "（已筛选）" : ""}`}
                    </span>
                    <div ref={toolbarRef} className="mawb-detail__toolbar-actions">
                        <span className="mawb-detail__muted">已选 {selectedRowKeys.length}</span>
                        <PermissionGuard permission="mawb:allocate">
                            <Button
                                size="small"
                                type="primary"
                                disabled={!selectedRowKeys.length}
                                onClick={openBatchAllocate}
                            >
                                批量分配
                            </Button>
                        </PermissionGuard>
                        <PermissionGuard permission="mawb:allocate">
                            <Button
                                size="small"
                                danger
                                disabled={!selectedRowKeys.length}
                                loading={deallocateMutation.isLoading}
                                onClick={handleBatchDeallocate}
                            >
                                批量收回
                            </Button>
                        </PermissionGuard>
                        <PermissionGuard permission="mawb:read">
                            <FilteredExportButton
                                resourceType="mawb_usages"
                                filterParams={exportFilterParams}
                                beforeExport={handleBeforeExport}
                                buttonText="导出Excel"
                                buttonProps={{ size: "small", disabled: !stockId || usageTotal === 0 }}
                            />
                        </PermissionGuard>
                    </div>
                </div>
                <div ref={usageTableRef}>
                    <Table
                        size="small"
                        rowKey="id"
                        columns={columns}
                        dataSource={usageItems}
                        loading={usageLoading}
                        rowSelection={rowSelection}
                        pagination={false}
                        scroll={{ x: 850, y: usageTableScrollY }}
                        footer={() =>
                            usageStale ? (
                                <div style={{ textAlign: "center", color: "#999" }}>刷新没有成功，表格里还是上一次的结果</div>
                            ) : usageTotal > 0 ? (
                                <div style={{ textAlign: "center" }}>
                                    {usageLoadingMore || loadingAllUsages ? (
                                        <span>正在加载…</span>
                                    ) : usageHasMore ? (
                                        <Space>
                                            <Button type="link" onClick={loadMoreUsages}>
                                                向下滚动或点击加载更多
                                            </Button>
                                            <Button type="link" onClick={handleLoadAllUsages}>
                                                加载全部 {usageTotal} 条
                                            </Button>
                                        </Space>
                                    ) : (
                                        <span style={{ color: "#999" }}>已加载全部 {usageTotal} 条</span>
                                    )}
                                </div>
                            ) : null
                        }
                    />
                </div>
            </Card>
            </div>

            {stock && (
                <MawbAllocationModal
                    open={allocModalOpen}
                    autoGuide={allocAutoGuide}
                    onCancel={() => {
                        setAllocModalOpen(false);
                        setAllocUsageIds(null);
                        setAllocAutoGuide(false);
                    }}
                    onSuccess={() => {
                        setAllocModalOpen(false);
                        setAllocUsageIds(null);
                        setAllocAutoGuide(false);
                        handleRefreshAll();
                    }}
                    stock={stock}
                    usageIds={allocUsageIds}
                />
            )}

            <Tour
                open={tourOpen}
                steps={tourSteps}
                onClose={() => setTourOpen(false)}
                onFinish={() => {
                    setTourOpen(false);
                    setAllocUsageIds(null);
                    setAllocModalOpen(true);
                    setAllocAutoGuide(true);
                }}
            />
        </div>
    );
};

export default MawbStockDetailPage;
